import { useQuery } from '@tanstack/react-query';
import {
  ExternalNetworkEntityKindSchema,
  type AssetGraphResponseDto,
} from '@terra/shared-contracts-external-network';
import {
  resolveGraphNodeExpansion,
  type GraphNodeExpansion,
} from '@terra/ui-graphs/external-network';
import { terraProxyApiClient } from '../../clients/testerClient';
import {
  buildExternalNetworkGraphQuery,
  EXPLORE_NEIGHBORHOOD_INITIAL_DEPTH,
} from '../../utils/external-network-graph.utils';
import { EXTERNAL_NETWORK_GRAPH_KEY } from './useExternalNetworkGraph';

export function useExternalNetworkNodeExpansion({
  surfaceId,
  graph,
  nodeId,
}: {
  surfaceId: string | null;
  graph: AssetGraphResponseDto | undefined;
  nodeId: string | null;
}): GraphNodeExpansion | undefined {
  const graphNode = nodeId
    ? graph?.nodes.find(
        (node) => node.id === nodeId && node.nodeType !== 'cluster',
      )
    : undefined;
  const parsedKind = ExternalNetworkEntityKindSchema.safeParse(graphNode?.kind);
  const neighborhoodQuery =
    graphNode && parsedKind.success
      ? buildExternalNetworkGraphQuery({
          viewMode: 'full',
          neighborhoodRoot: { id: graphNode.id, kind: parsedKind.data },
          neighborhoodDepth: EXPLORE_NEIGHBORHOOD_INITIAL_DEPTH,
          expandedClusterIds: [],
        })
      : undefined;
  const { data, isPending } = useQuery({
    queryKey: [EXTERNAL_NETWORK_GRAPH_KEY, surfaceId, neighborhoodQuery],
    queryFn: () =>
      terraProxyApiClient.getExternalNetworkGraph(
        surfaceId ?? '',
        neighborhoodQuery,
      ),
    enabled: Boolean(surfaceId) && Boolean(neighborhoodQuery),
  });

  if (!neighborhoodQuery) {
    return undefined;
  }

  return resolveGraphNodeExpansion({ graph, neighborhood: data, isPending });
}
